// stores/driverScorePeriodicStore.js
'use client';

import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcherPost = async ([url, payloadStr]) => {
  try {
    const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response?.data ?? response;
  } catch (err) {
    console.error('API fetcher error:', { url, payloadStr, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateDriverScorePeriodicList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/driver-score-periodic/search');
};

export const useDriverScorePeriodicList = (page = 0, rowsPerPage = 10, searchKey = 'driver_name', searchValue = '', companyId = '') => {
  const start = page * rowsPerPage;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).setOrder(['id'], false);

    if (searchValue && searchValue.trim() !== '') {
      fb.like(searchKey || 'driver_name', searchValue, 'AND');
    }

    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);

  const key = useMemo(() => ['/driver-score-periodic/search', JSON.stringify(payload)], [payload]);

  const {
    data,
    error,
    isLoading,
    mutate: revalidate
  } = useSWR(key, fetcherPost, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  const total = data?.totalRecord ?? data?.totalRecords ?? data?.recordsTotal ?? data?.dataSize ?? rows.length;

  return {
    data: rows,
    totalRecord: total,
    isLoading,
    isError: error,
    revalidate
  };
};

export const createDriverScorePeriodic = async (payload) => {
  try {
    const response = await fleetApi.post(`/driver-score-periodic/new${getTzParam()}`, payload);
    revalidateDriverScorePeriodicList();
    return response.data;
  } catch (error) {
    console.error('Error creating driver score periodic:', error);
    throw error;
  }
};

export const updateDriverScorePeriodic = async (id, payload) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.put(`/driver-score-periodic/edit/${id}${getTzParam()}`, payload);
    revalidateDriverScorePeriodicList();
    return response.data;
  } catch (error) {
    console.error('Error updating driver score periodic:', error);
    throw error;
  }
};

export const deleteDriverScorePeriodic = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/driver-score-periodic/delete/${id}${getTzParam()}`);
    revalidateDriverScorePeriodicList();
    return response.data;
  } catch (error) {
    console.error('Error deleting driver score periodic:', error);
    throw error;
  }
};

export const getDriverScorePeriodicById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/driver-score-periodic/${id}${getTzParam()}`);
    return response.data?.data ?? response.data ?? response;
  } catch (error) {
    console.error('Error fetching driver score periodic by id:', error);
    throw error;
  }
};
